import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { z } from 'zod';

import { Require } from '~/app/require';
import { safeRedirect } from '~/features/auth/redirect';
import { returnedFailure } from '~/features/auth/web-doors';
import { linkCodeOf } from '~/features/get-started/code-rules';
import { GetStartedPage } from '~/features/get-started/get-started-page';

/**
 * The router reads each query value as JSON, so `code=393233` arrives as a
 * number while `code=012345` stays a string. Both are the same six digits.
 * A vendor sends a failed sign-in back as `?door=google&error=…`; the gate
 * sends a person here with `?redirect=`; the email's link carries `?email=&code=`.
 */
const search = z.object({
  redirect: z.string().optional(),
  door: z.string().optional(),
  error: z.string().optional(),
  email: z.string().optional(),
  code: z.union([z.string(), z.number()]).optional(),
  // The invitation's email door opens this page already past the doors.
  via: z.literal('email').optional(),
});

function GetStartedRoute(): ReactElement {
  const { redirect, door, error, email, code, via } = Route.useSearch();
  // A person with no name is let in as well: the page asks for it, and the
  // gate sends everybody else where they belong.
  return (
    <Require allow={['signed-out', 'needs-name']}>
      <GetStartedPage
        returned={returnedFailure(door, error)} link={linkCodeOf(email, code)}
        viaEmail={via === 'email'}
        redirect={safeRedirect(redirect)}
      />
    </Require>
  );
}

export const Route = createFileRoute('/get-started')({
  validateSearch: search,
  component: GetStartedRoute,
});
